<template>
    <div class="container_width questionnaire_y_margin">
        <div id="questionnaire">
            <ModuleQuestionnaire
                v-if="loadQuestionnaire"
                :allowImpersonation="true"
                :userId="user_id"
                :moduleId="module_id"
                :moduleKey="module_key"
                :projectId="project_id"
                :date_submitted="details.date_submitted"
                :status="details.status"
                :readonly="false"
                :questionsOnly="questionsOnlyMode"
                :getCurrentProject="getCurrentProject"
                :openQuestionnaire="openQuestionnaire"
                @questionnaireDisplayProgressUpdated="forwardDisplayProgressUpdate"
                @questionnaireProgressSaved="forwardSavedProgressUpdate" />
            <!-- :readonly="readonly" CHANGED TO false FOR ADMIN -->
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import Cookie from 'js-cookie'
    import appUrls from '../helpers/environment'
    import router from '../router'
    import { safeParseJson } from '../helpers/safe-json'

    import ModuleQuestionnaire from './questionnaire_template/questionnaire'

    export default {
        name: 'BuyerQuestionnaire',

        components: {
            ModuleQuestionnaire
        },

        props: {
            user_id: String,
            module_key: String,
            module_id: String,
            project_id: String,
            openQuestionnaire: {
                type: Function,
                default: () => {}
            },
            questionsOnly: {
                type: Boolean,
                default: false
            },
            isOpen: {
                type: Boolean,
                default: true
            }
        },

        computed: {
            questionsOnlyMode () {
                return this.questionsOnly || !this.project_id || !this.user_id
            }
        },

        data () {
            return {
                under_review: false,
                loadQuestionnaire: false,
                progress_active: true,
                details: [],
                progressSyncTimer: null,
                lastSyncedProgressKey: ''
            }
        },

        mounted() {
            // intentionally left blank; reload handled by prop watchers
        },

        watch: {
            project_id: {
                immediate: true,
                handler() {
                    if (this.isOpen === false) return
                    this.loadQuestionnaire = false
                    if ( this.questionsOnlyMode ) {
                        this.loadQuestionnaireWithoutProjectContext()
                        return
                    }
                    this.getCurrentProject()
                }
            },
            module_id(val) {
                if (val && this.isOpen !== false) {
                    this.loadQuestionnaire = false
                    if ( this.questionsOnlyMode ) {
                        this.loadQuestionnaireWithoutProjectContext()
                        return
                    }
                    this.getCurrentProject()
                }
            },
            user_id(val) {
                if (val && this.isOpen !== false) {
                    this.loadQuestionnaire = false
                    if ( this.questionsOnlyMode ) {
                        this.loadQuestionnaireWithoutProjectContext()
                        return
                    }
                    this.getCurrentProject()
                }
            },
            isOpen(val, oldVal) {
                if (val && !oldVal && this.questionsOnlyMode) {
                    this.loadQuestionnaire = false
                    this.loadQuestionnaireWithoutProjectContext()
                    return
                }

                if (val && !oldVal && this.project_id && this.user_id) {
                    this.emitQuestionnaireProgressUpdate()
                    this.loadQuestionnaire = false
                    this.getCurrentProject()
                }
            }
        },

        methods: {
            normalizeQuestionnaireProgress: function (value) {
                const parsed = safeParseJson(value, value)
                return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : undefined
            },

            resolveProgressConflict: function (currentProgress, incomingProgress) {
                if ( !currentProgress ) return incomingProgress
                if ( !incomingProgress ) return currentProgress

                const currentCalculatedAt = Date.parse(currentProgress.calculated_at || '')
                const incomingCalculatedAt = Date.parse(incomingProgress.calculated_at || '')
                const hasCurrentCalculatedAt = Number.isFinite(currentCalculatedAt)
                const hasIncomingCalculatedAt = Number.isFinite(incomingCalculatedAt)

                if ( hasCurrentCalculatedAt && hasIncomingCalculatedAt ) {
                    return incomingCalculatedAt >= currentCalculatedAt ? incomingProgress : currentProgress
                }

                const currentFinished = Number.isFinite(Number(currentProgress.finished)) ? Number(currentProgress.finished) : 0
                const incomingFinished = Number.isFinite(Number(incomingProgress.finished)) ? Number(incomingProgress.finished) : 0
                const currentTotal = Number.isFinite(Number(currentProgress.total)) ? Number(currentProgress.total) : 0
                const incomingTotal = Number.isFinite(Number(incomingProgress.total)) ? Number(incomingProgress.total) : 0

                // Fallback anti-regression guard when timestamps are missing.
                if ( incomingTotal === currentTotal && incomingFinished < currentFinished ) {
                    return currentProgress
                }

                return incomingProgress
            },

            emitQuestionnaireProgressUpdate: function () {
                const normalizedProgress = this.normalizeQuestionnaireProgress(this.details && this.details.questionnaire_progress)
                const parsedSections = safeParseJson(this.details && this.details.questionnaire_sections, this.details && this.details.questionnaire_sections)

                const hasProgress = normalizedProgress !== undefined
                const hasSections = Array.isArray(parsedSections)
                const hasProfilePercentage = this.details && this.details.profile_percentage !== undefined
                const hasStatus = this.details && this.details.status !== undefined
                const hasDateSubmitted = this.details && this.details.date_submitted !== undefined

                this.$emit('questionnaireProgressUpdated', {
                    questionnaire_progress: hasProgress ? normalizedProgress : undefined,
                    questionnaire_sections: hasSections ? parsedSections : undefined,
                    profile_percentage: hasProfilePercentage ? this.details.profile_percentage : undefined,
                    status: hasStatus ? this.details.status : undefined,
                    date_submitted: hasDateSubmitted ? this.details.date_submitted : undefined
                })
            },

            forwardDisplayProgressUpdate: function (payload) {
                const finished = Number.parseInt(payload && payload.finished, 10)
                const total = Number.parseInt(payload && payload.total, 10)
                const normalizedFinished = Number.isFinite(finished) && finished > 0 ? finished : 0
                const normalizedTotal = Number.isFinite(total) && total > 0 ? total : 0

                if (!normalizedTotal && !normalizedFinished) return

                const currentProgress = this.normalizeQuestionnaireProgress(this.details && this.details.questionnaire_progress) || {}
                const resolvedTotal = Math.max(normalizedTotal, normalizedFinished)

                this.$emit('questionnaireProgressUpdated', {
                    questionnaire_progress: {
                        ...currentProgress,
                        finished: normalizedFinished,
                        total: resolvedTotal,
                        ready: resolvedTotal > 0 ? normalizedFinished >= resolvedTotal : false
                    }
                })

                this.queueProgressSync({
                    finished: normalizedFinished,
                    total: resolvedTotal,
                    ready: resolvedTotal > 0 ? normalizedFinished >= resolvedTotal : false
                })
            },

            forwardSavedProgressUpdate: function (payload) {
                const progress = payload && payload.questionnaire_progress && typeof payload.questionnaire_progress === 'object'
                    ? payload.questionnaire_progress
                    : null

                if ( !progress ) return

                this.details = {
                    ...(this.details || {}),
                    questionnaire_progress: progress,
                    questionnaire_sections: Array.isArray(payload.questionnaire_sections) ? payload.questionnaire_sections : (this.details && this.details.questionnaire_sections)
                }

                this.$emit('questionnaireProgressUpdated', {
                    questionnaire_progress: progress,
                    questionnaire_sections: Array.isArray(payload.questionnaire_sections) ? payload.questionnaire_sections : undefined
                })

                this.queueProgressSync({
                    finished: Number.isFinite(Number(progress.finished)) ? Number(progress.finished) : 0,
                    total: Number.isFinite(Number(progress.total)) ? Number(progress.total) : 0,
                    ready: typeof progress.ready === 'boolean' ? progress.ready : false
                })
            },

            queueProgressSync: function (progress) {
                if ( this.progressSyncTimer ) clearTimeout(this.progressSyncTimer)
                this.progressSyncTimer = setTimeout(() => {
                    this.syncQuestionnaireProgress(progress)
                }, 350)
            },

            syncQuestionnaireProgress: async function (progress) {
                try {
                    if ( this.questionsOnlyMode || !this.project_id || !this.user_id ) return

                    const syncKey = `${progress.finished}:${progress.total}:${progress.ready ? 1 : 0}`
                    if ( syncKey === this.lastSyncedProgressKey ) return

                    const sync_rs = await axios.post(`${appUrls.api}/api/sync_questionnaire_progress`, {
                        headers: { authentication: Cookie.get('token'), 'Content-Type': 'application/json' },
                        projectId: this.project_id,
                        userId: this.user_id,
                        moduleId: this.module_id,
                        moduleKey: this.module_key,
                        su: true,
                        progress
                    })

                    if ( sync_rs.data && sync_rs.data.outcome ) {
                        this.lastSyncedProgressKey = syncKey
                        this.details = {
                            ...(this.details || {}),
                            questionnaire_progress: sync_rs.data.questionnaire_progress,
                            questionnaire_sections: sync_rs.data.questionnaire_sections
                        }
                        this.emitQuestionnaireProgressUpdate()
                    }
                } catch ( error ) {
                    console.log(error)
                }
            },

            loadQuestionnaireWithoutProjectContext: function () {
                this.details = {
                    questionnaire_progress: null,
                    questionnaire_sections: null,
                    profile_percentage: 0,
                    status: '',
                    date_submitted: null
                }
                this.lastSyncedProgressKey = ''
                this.emitQuestionnaireProgressUpdate()

                setTimeout(() => {
                    this.loadQuestionnaire = true
                }, 100)
            },

            getCurrentProject: async function () {
                if ( this.questionsOnlyMode || !this.project_id || !this.user_id ) {
                    this.loadQuestionnaireWithoutProjectContext()
                    return
                }

                await axios.get(`${appUrls.api}/api/current_project/${this.project_id}/${this.user_id}`, {
                    headers: { authentication: Cookie.get('token'), 'Content-Type': 'application/json' }
                })
                .then(res => {
                    if (res.data.outcome) {
                        const fetchedDetails = res.data.details || {}
                        const currentProgress = this.normalizeQuestionnaireProgress(this.details && this.details.questionnaire_progress)
                        const fetchedProgress = this.normalizeQuestionnaireProgress(fetchedDetails.questionnaire_progress)
                        const resolvedProgress = this.resolveProgressConflict(currentProgress, fetchedProgress)

                        this.details = {
                            ...fetchedDetails,
                            questionnaire_progress: resolvedProgress || fetchedDetails.questionnaire_progress
                        }
                        this.emitQuestionnaireProgressUpdate()
                        setTimeout(() => {
                            this.loadQuestionnaire = true
                        }, 500)
                    }
                })
                .catch(function (error) {
                    console.log(error)
                })
            },

            navigate() {
                router.go(-1)
            }
        },

        beforeDestroy () {
            if ( this.progressSyncTimer ) clearTimeout(this.progressSyncTimer)
        }
    }
</script>

<style scoped>
    h1, h2 {
        font-weight: normal;
    }

    ul {
        list-style-type: none;
        padding: 0;
    }

    li {
        display: inline-block;
        margin: 0 10px;
    }

    #questionnaire {
        /* min-height: 30vh; */
        background-color: #FFFFFF;
        padding: 0;
    }

    .questionnaire_y_margin {
        margin-top: 20px;
        /* margin-bottom: 10px; */
    }
    .questionnaire_title {
        font-family: 'SansationBold';
        color: #37383C;
        font-size: 2.2rem;
    }
    .container_width {
        width: 100%;
    }

    #module_nav {
        background-color: transparent;
    }
    .section-wrapper {
        margin: 30px auto;
    }
</style>



// WEBPACK FOOTER //
// src/components/ModuleQuestionnaire.vue